import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import { useTranslation } from 'react-i18next';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import VehicleImage from '../../components/base/VehicleImage';
import { featuredVehicles } from '../../mocks/vehicles';
import { vehiclesForSale } from '../../mocks/vehicles-for-sale';

export default function HomePage() {
  const { t } = useTranslation('common');

  const p = (url: string) => `https://images.weserv.nl/?url=${url.replace('https://', '')}&output=webp&q=80&n=-1`;

  const heroImages = [
    p('https://sunandspeed.com/wp-content/uploads/2025/04/PRINCIPAL-lamborghini-6-1.webp'),
    p('https://sunandspeed.com/wp-content/uploads/2025/04/IMG_3231.webp'),
    p('https://sunandspeed.com/wp-content/uploads/2025/04/7.webp')
  ];

  const brands = [
    { name: 'Lamborghini', logo: p('https://sunandspeed.com/wp-content/uploads/2025/04/ASDFASDAFD.webp') },
    { name: 'Ferrari', logo: p('https://sunandspeed.com/wp-content/uploads/2025/04/asdonijapso-copia.webp') },
    { name: 'McLaren', logo: p('https://sunandspeed.com/wp-content/uploads/2025/04/aposdijfas-copia.webp') },
    { name: 'Porsche', logo: p('https://sunandspeed.com/wp-content/uploads/2025/04/japosdia.webp') }
  ];

  return (
    <div className="min-h-screen bg-black">
      <Header />
      <WhatsAppButton />

      {/* Hero Section */}
      <section className="relative h-screen">
        <Swiper
          modules={[Autoplay, Pagination, Navigation]}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          navigation
          loop
          className="h-full"
        >
          {heroImages.map((image, index) => (
            <SwiperSlide key={index}>
              <div className="relative h-full bg-gray-900">
                <img
                  src={image}
                  alt={`${t('hero.title')} ${index === 0 ? 'Lamborghini' : index === 1 ? 'Ferrari' : 'McLaren'} ${t('hero.titleLocation')}`}
                  title={`${index === 0 ? 'Lamborghini Urus' : index === 1 ? 'Ferrari Roma' : 'McLaren 720S'} - VIP Luxury Cars MKS`}
                  className="w-full h-full object-cover"
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent"></div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="absolute inset-0 flex items-center z-10">
          <div className="max-w-7xl mx-auto px-4 w-full">
            <div className="max-w-2xl">
              <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                <strong>{t('hero.title')}</strong> {t('nav.home').toLowerCase()}
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">
                  {t('hero.titleLocation')}
                </span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                {t('hero.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/flota"
                  className="bg-gradient-to-r from-amber-500 to-amber-600 text-black px-8 py-4 rounded-full font-bold text-lg hover:from-amber-600 hover:to-amber-700 transition-all text-center whitespace-nowrap cursor-pointer"
                >
                  {t('hero.viewFleet')}
                </Link>
                <a
                  href="https://wa.me/522291578562?text=Hola!%20Vi%20la%20p%C3%A1gina%20de%20VIP%20Luxury%20Cars%20MKS%20y%20quiero%20alquilar%20un%20auto%20de%20lujo.%20%C2%BFQu%C3%A9%20opciones%20tienen%20disponibles%3F"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-green-600 transition-all text-center flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-2xl"></i>
                  {t('hero.reserveWhatsApp')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Brands */}
      <section className="py-16 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-white mb-12">
            <strong>{t('home.brands.title')}</strong> {t('home.brands.subtitle')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {brands.map((brand) => (
              <div key={brand.name} className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 flex items-center justify-center hover:bg-white/10 transition-all">
                <img
                  src={brand.logo}
                  alt={`${t('hero.title')} ${brand.name} ${t('hero.titleLocation')}`}
                  title={`${brand.name} - VIP Luxury Cars MKS`}
                  className="w-full h-20 object-contain filter brightness-0 invert opacity-80 hover:opacity-100 transition-opacity"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Vehicles */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              <strong>{t('home.featured.title')}</strong> <span className="text-amber-400">{t('home.featured.titleHighlight')}</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {t('home.featured.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
            {featuredVehicles.map((vehicle) => (
              <article
                key={vehicle.id}
                className="bg-black rounded-2xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300 border border-amber-900/30"
              >
                <div className="relative h-64 overflow-hidden">
                  <Swiper
                    modules={[Autoplay, Pagination]}
                    autoplay={{ delay: 3000, disableOnInteraction: false }}
                    pagination={{ clickable: true }}
                    loop
                    className="h-full"
                  >
                    {vehicle.images.map((image, index) => (
                      <SwiperSlide key={index}>
                        <div className="relative h-64 bg-gray-900">
                          <VehicleImage
                            src={image}
                            alt={`${t('hero.title')} ${vehicle.brand} ${vehicle.model} ${t('hero.titleLocation')} - Foto ${index + 1}`}
                            title={`${vehicle.brand} ${vehicle.model} - ${vehicle.pricePerDay.toLocaleString()} ${t('home.featured.perDay')}`}
                            className="w-full h-full object-cover object-center absolute inset-0"
                          />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                  <div className="absolute top-4 right-4 bg-amber-500 text-black px-4 py-2 rounded-full font-bold text-sm z-10">
                    ${vehicle.pricePerDay.toLocaleString()}{t('home.featured.perDay')}
                  </div>
                </div>

                <div className="p-6">
                  <div className="mb-2">
                    <span className="text-amber-400 text-sm font-semibold">{vehicle.category}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    <strong>{vehicle.brand}</strong>
                  </h3>
                  <p className="text-xl text-gray-300 mb-4">{vehicle.model}</p>
                  <p className="text-gray-400 text-sm mb-6 line-clamp-2">
                    {vehicle.description}
                  </p>

                  <div className="flex items-center justify-between text-sm text-gray-400 mb-6">
                    <div className="flex items-center gap-2">
                      <i className="ri-user-line"></i>
                      <span>{vehicle.specifications.passengers} {t('home.featured.passengers')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-settings-line"></i>
                      <span>{vehicle.specifications.transmission}</span>
                    </div>
                  </div>

                  <Link
                    to={`/vehiculo/${vehicle.id}`}
                    className="block w-full bg-gradient-to-r from-amber-500 to-amber-600 text-black text-center py-3 rounded-full font-bold hover:from-amber-600 hover:to-amber-700 transition-all whitespace-nowrap cursor-pointer"
                  >
                    {t('home.featured.viewDetails')}
                  </Link>
                </div>
              </article>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/flota"
              className="inline-block bg-white/10 text-white px-8 py-4 rounded-full font-bold hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
            >
              {t('home.featured.viewAll')}
            </Link>
          </div>
        </div>
      </section>

      {/* Vehicles For Sale Section */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              <strong>{t('home.forSale.title')}</strong> <span className="text-amber-400">{t('home.forSale.titleHighlight')}</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {t('home.forSale.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {vehiclesForSale.map((vehicle) => (
              <article
                key={vehicle.id}
                className="bg-gradient-to-br from-gray-900 to-black rounded-2xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300 border border-amber-900/30"
              >
                <div className="relative h-64 overflow-hidden">
                  <Swiper
                    modules={[Autoplay, Pagination]}
                    autoplay={{ delay: 3500, disableOnInteraction: false }}
                    pagination={{ clickable: true }}
                    loop
                    className="h-full"
                  >
                    {vehicle.images.map((image, index) => (
                      <SwiperSlide key={index}>
                        <div className="relative h-64 bg-gray-900">
                          <VehicleImage
                            src={image}
                            alt={`${vehicle.brand} ${vehicle.model} ${t('common.forSale')} - Foto ${index + 1}`}
                            className="w-full h-full object-cover object-center absolute inset-0"
                          />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                  <div className="absolute top-4 left-4 bg-red-600 text-white px-4 py-2 rounded-full font-bold text-sm z-10">
                    {t('home.forSale.forSale')}
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-1">
                    <strong>{vehicle.brand}</strong>
                  </h3>
                  <p className="text-lg text-amber-400 mb-3">{vehicle.model}</p>
                  
                  {vehicle.year && (
                    <p className="text-gray-400 text-sm mb-2">{t('home.forSale.year')}: {vehicle.year}</p>
                  )}
                  {vehicle.mileage && (
                    <p className="text-gray-400 text-sm mb-4">{vehicle.mileage}</p>
                  )}

                  <div className="mb-4 p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                    <p className="text-amber-400 font-bold text-lg">{vehicle.price}</p>
                  </div>

                  <a
                    href={`https://wa.me/522291578562?text=${encodeURIComponent(`${t('home.forSale.interested')} ${vehicle.brand} ${vehicle.model} ${t('common.forSale')}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-gradient-to-r from-amber-500 to-amber-600 text-black text-center py-3 rounded-full font-bold hover:from-amber-600 hover:to-amber-700 transition-all text-sm whitespace-nowrap cursor-pointer"
                  >
                    {t('home.forSale.moreInfo')}
                  </a>
                </div>
              </article>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/vehiculos-en-venta"
              className="inline-block bg-white/10 text-white px-8 py-4 rounded-full font-bold hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
            >
              {t('home.forSale.viewAll')}
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl lg:text-5xl font-bold text-center text-white mb-16">
            {t('home.whyChoose.title')} <strong>{t('home.whyChoose.titleHighlight')}</strong>?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-amber-500/10 rounded-full mx-auto mb-6">
                <i className="ri-vip-crown-line text-4xl text-amber-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3"><strong>{t('home.whyChoose.feature1Title')}</strong></h3>
              <p className="text-gray-400">
                {t('home.whyChoose.feature1Text')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-amber-500/10 rounded-full mx-auto mb-6">
                <i className="ri-shield-check-line text-4xl text-amber-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3"><strong>{t('home.whyChoose.feature2Title')}</strong></h3>
              <p className="text-gray-400">
                {t('home.whyChoose.feature2Text')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-amber-500/10 rounded-full mx-auto mb-6">
                <i className="ri-customer-service-2-line text-4xl text-amber-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3"><strong>{t('home.whyChoose.feature3Title')}</strong></h3>
              <p className="text-gray-400">
                {t('home.whyChoose.feature3Text')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-amber-500/10 rounded-full mx-auto mb-6">
                <i className="ri-map-pin-line text-4xl text-amber-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3"><strong>{t('home.whyChoose.feature4Title')}</strong></h3>
              <p className="text-gray-400">
                {t('home.whyChoose.feature4Text')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* International Expansion */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-gradient-to-r from-amber-900/20 to-amber-800/20 rounded-3xl p-8 lg:p-12 border border-amber-700/30">
            <div className="text-center max-w-4xl mx-auto">
              <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">
                <strong>{t('home.expansion.title')}</strong> {t('home.expansion.titleHighlight')}
              </h2>
              <p className="text-xl text-gray-300 mb-4">
                {t('home.expansion.text1')}
              </p>
              <p className="text-lg text-gray-400 mb-8">
                {t('home.expansion.text2')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/flota"
                  className="bg-gradient-to-r from-amber-500 to-amber-600 text-black px-8 py-4 rounded-full font-bold hover:from-amber-600 hover:to-amber-700 transition-all whitespace-nowrap cursor-pointer"
                >
                  {t('home.expansion.exploreFleet')}
                </Link>
                <Link
                  to="/contacto"
                  className="bg-white/10 text-white px-8 py-4 rounded-full font-bold hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
                >
                  {t('home.expansion.contactNow')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl lg:text-5xl font-bold text-center text-white mb-16">
            {t('home.testimonials.title')} <span className="text-amber-400">{t('home.testimonials.titleHighlight')}</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <article className="bg-gradient-to-br from-gray-900 to-black p-8 rounded-2xl border border-amber-900/30">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-amber-400 text-xl"></i>
                ))}
              </div>
              <p className="text-gray-300 mb-6">
                "{t('home.testimonials.testimonial1')}"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-amber-400 text-xl"></i>
                </div>
                <div>
                  <p className="text-white font-semibold">{t('home.testimonials.testimonial1Name')}</p>
                  <p className="text-gray-400 text-sm">{t('home.testimonials.testimonial1Location')}</p>
                </div>
              </div>
            </article>

            <article className="bg-gradient-to-br from-gray-900 to-black p-8 rounded-2xl border border-amber-900/30">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-amber-400 text-xl"></i>
                ))}
              </div>
              <p className="text-gray-300 mb-6">
                "{t('home.testimonials.testimonial2')}"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-amber-400 text-xl"></i>
                </div>
                <div>
                  <p className="text-white font-semibold">{t('home.testimonials.testimonial2Name')}</p>
                  <p className="text-gray-400 text-sm">{t('home.testimonials.testimonial2Location')}</p>
                </div>
              </div>
            </article>

            <article className="bg-gradient-to-br from-gray-900 to-black p-8 rounded-2xl border border-amber-900/30">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-amber-400 text-xl"></i>
                ))}
              </div>
              <p className="text-gray-300 mb-6">
                "{t('home.testimonials.testimonial3')}"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-amber-400 text-xl"></i>
                </div>
                <div>
                  <p className="text-white font-semibold">{t('home.testimonials.testimonial3Name')}</p>
                  <p className="text-gray-400 text-sm">{t('home.testimonials.testimonial3Location')}</p>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl lg:text-5xl font-bold text-center text-white mb-12">
            {t('home.faq.title')} <span className="text-amber-400">{t('home.faq.titleHighlight')}</span>
          </h2>

          <div className="space-y-6">
            <article className="bg-black rounded-2xl p-6 border border-amber-900/30">
              <h3 className="text-xl font-bold text-white mb-3">
                {t('home.faq.q1')}
              </h3>
              <p className="text-gray-400">
                {t('home.faq.a1')}
              </p>
            </article>

            <article className="bg-black rounded-2xl p-6 border border-amber-900/30">
              <h3 className="text-xl font-bold text-white mb-3">
                {t('home.faq.q2')}
              </h3>
              <p className="text-gray-400">
                {t('home.faq.a2')}
              </p>
            </article>

            <article className="bg-black rounded-2xl p-6 border border-amber-900/30">
              <h3 className="text-xl font-bold text-white mb-3">
                {t('home.faq.q3')}
              </h3>
              <p className="text-gray-400">
                {t('home.faq.a3')}
              </p>
            </article>

            <article className="bg-black rounded-2xl p-6 border border-amber-900/30">
              <h3 className="text-xl font-bold text-white mb-3">
                {t('home.faq.q4')}
              </h3>
              <p className="text-gray-400">
                {t('home.faq.a4')}
              </p>
            </article>

            <article className="bg-black rounded-2xl p-6 border border-amber-900/30">
              <h3 className="text-xl font-bold text-white mb-3">
                {t('home.faq.q5')}
              </h3>
              <p className="text-gray-400">
                {t('home.faq.a5')}
              </p>
            </article>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}